import { useState } from "react";
import {
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  List,
  ListItem,
  Show,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { isPast } from "date-fns";
import { listingsModel } from "models/listings";
import type { APIListing } from "models/types/api-listing";
import { useParams } from "react-router-dom";
import BarChartIcon from "assets/icons/medium/bar-chart.svg?react";
import MissingDates from "assets/icons/medium/missing-dates.svg?react";
import { Empty } from "components/empty";
import { EmptyText } from "components/empty-text";
import {
  MultiSelectInput,
  type MenuOption,
} from "components/inputs/multi-select-input";
import { ScreenWrapper } from "components/screen-wrapper";
import { ShowMoreLessButton } from "components/show-more-less-button";
import { SkeletonGrid } from "components/skeleton-grid";
import { StatsCard } from "components/stats-card";
import { StatsCardSkeleton } from "components/stats-card-skeleton";
import { TotalCountBadge } from "components/total-count-badge";
import { useRegion } from "hooks/use-region";
import { SkeletonList } from "modules/vendor-portal/components/skeleton-list";
import { useWhichWordFactory } from "modules/vendor-portal/hooks/use-which-words";
import { VENDOR_PORTAL_PATHS } from "modules/vendor-portal/paths";
import { EventListItem } from "modules/vendor-portal/screens/events/components/event-list-item";
import type { FeedbackLocationState } from "modules/vendor-portal/screens/feedback/feedback-screen";
import { FeedbackGroupByOption } from "modules/vendor-portal/screens/feedback/utils";
import { EventCardSkeleton } from "modules/vendor-portal/screens/overview/components/open-home-events/event-card-skeleton";
import { generateSingularLabel } from "utils/generate-singular-label";

const EVENT_COUNTS_TO_SHOW = 3;

type EventType = NonNullable<APIListing["events"]>[number];

const REGION_EVENT_OPTIONS: Record<string, MenuOption[]> = {
  AUS: [
    { value: "private_inspection", label: "Inspection" },
    { value: "open_home", label: "Open home" },
    { value: "auction", label: "Auction" },
    { value: "set_sale", label: "Set sale" },
    { value: "tender", label: "Tender" },
  ],
  GBR: [
    { value: "private_inspection", label: "Accompanied Viewing" },
    { value: "unaccompanied_inspection", label: "Unaccompanied Viewing" },
    { value: "open_home", label: "Open home" },
    { value: "auction", label: "Auction" },
  ],
};

export function EventsScreen() {
  const { propertyId } = useParams();
  const { data, isLoading } = useQuery(listingsModel.queries.read(propertyId));
  const region = useRegion();
  const shouldShowOpenHomeData = data?.category?.id !== "commercial";

  let EVENT_OPTIONS = REGION_EVENT_OPTIONS[region];
  if (!shouldShowOpenHomeData) {
    EVENT_OPTIONS = EVENT_OPTIONS.filter(
      (option) => option.value !== "open_home",
    );
  }
  const DEFAULT_FILTERS = EVENT_OPTIONS.map((x) => x.value);
  const isMobile = useBreakpointValue({ base: true, sm: false });

  // Filter states
  const [pastEventFilters, setPastEventFilters] =
    useState<string[]>(DEFAULT_FILTERS);
  const [upcomingEventFilters, setUpcomingEventFilters] =
    useState<string[]>(DEFAULT_FILTERS);
  const [mobileFilters, setMobileFilters] = useState<string[]>(DEFAULT_FILTERS);

  // Show/hide states
  const [showAllPastEvents, setShowAllPastEvents] = useState(false);
  const [showAllUpcomingEvents, setShowAllUpcomingEvents] = useState(false);
  const ww = useWhichWordFactory();

  if (!data) return null;

  const summary = data?.summary;
  const inspectionsHeld =
    summary?.count_of_inspections_and_open_homes?.value || 0;

  const summaryStats = [
    {
      id: "inspections",
      label: `${region === "GBR" ? "Viewing" : "Inspection"}${
        inspectionsHeld !== 1 ? "s" : ""
      }${
        shouldShowOpenHomeData
          ? " / open home" + (inspectionsHeld !== 1 ? "s " : " ")
          : ""
      }${" held"}`,
      value: inspectionsHeld,
    },
    {
      id: "total_attendance_count",
      label: "Total attendance count",
      value:
        summary?.count_of_people_who_have_inspected_including_duplicates
          ?.value || 0,
      navigateTo: {
        to: `../${VENDOR_PORTAL_PATHS.FEEDBACK}`,
        navigateOptions: {
          state: {
            filters: ["ofi", "inspection"],
            groupBy: FeedbackGroupByOption.Name,
          } satisfies FeedbackLocationState,
        },
      },
    },
    {
      id: "attendees",
      label: "Total attendees",
      value: summary?.count_of_unique_people_who_have_inspected?.value || 0,
      navigateTo: {
        to: `../${VENDOR_PORTAL_PATHS.FEEDBACK}`,
        navigateOptions: {
          state: {
            filters: ["ofi", "inspection"],
            groupBy: FeedbackGroupByOption.Name,
          } satisfies FeedbackLocationState,
        },
      },
    },
    {
      id: "total_repeat_attendees",
      label: "Total repeat attendees",
      value:
        summary?.count_of_unique_people_who_have_inspected_more_than_once
          ?.value || 0,
      navigateTo: {
        to: `../${VENDOR_PORTAL_PATHS.FEEDBACK}`,
        navigateOptions: {
          state: {
            groupBy: FeedbackGroupByOption.Name,
            showOnly: {
              key: "repeat_attendee",
            },
          } satisfies FeedbackLocationState,
        },
      },
    },
  ];

  // Event filtering logic
  const pastEvents =
    data.events
      ?.filter((event: EventType) => isPast(new Date(event.starts_at)))
      ?.sort(
        (a: EventType, b: EventType) =>
          new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime(),
      ) || [];

  const filteredPastEvents = pastEvents.filter((event: EventType) =>
    isMobile
      ? mobileFilters.includes(event.type.id)
      : pastEventFilters.includes(event.type.id),
  );

  const upcomingEvents =
    data.events?.filter(
      (event: EventType) => !isPast(new Date(event.starts_at)),
    ) || [];

  const filteredUpcomingEvents = upcomingEvents.filter((event: EventType) =>
    isMobile
      ? mobileFilters.includes(event.type.id)
      : upcomingEventFilters.includes(event.type.id),
  );
  const localeEventsWords = ww("events");

  // Limit displayed events based on show/hide state
  const pastEventsToShow = showAllPastEvents
    ? filteredPastEvents
    : filteredPastEvents.slice(0, EVENT_COUNTS_TO_SHOW);
  const upcomingEventsToShow = showAllUpcomingEvents
    ? filteredUpcomingEvents
    : filteredUpcomingEvents.slice(0, EVENT_COUNTS_TO_SHOW);

  // Render past events list
  const renderPastEvents = (variant?: string) => (
    <SkeletonList
      isLoaded={!isLoading}
      skeleton={<EventCardSkeleton />}
      spacing={4}
      listSize={4}
    >
      <List spacing={4}>
        {filteredPastEvents.length === 0 && pastEvents.length > 0 ? (
          <EmptyText textAlign={"center"}>
            No {localeEventsWords.toLowerCase()} found
          </EmptyText>
        ) : (
          <>
            {pastEventsToShow.map((event: EventType) => (
              <ListItem key={event.id}>
                <EventListItem event={event} variant={variant} />
              </ListItem>
            ))}
            {pastEvents.length > EVENT_COUNTS_TO_SHOW ? (
              <ShowMoreLessButton
                showAll={showAllPastEvents}
                setShowAll={setShowAllPastEvents}
              />
            ) : null}
          </>
        )}
      </List>
    </SkeletonList>
  );

  // Render upcoming events list
  const renderUpcomingEvents = () => (
    <SkeletonList
      isLoaded={!isLoading}
      skeleton={<EventCardSkeleton />}
      spacing={4}
      listSize={4}
    >
      <List spacing={4}>
        {filteredUpcomingEvents.length === 0 && upcomingEvents.length > 0 ? (
          <EmptyText textAlign={"center"}>
            No {localeEventsWords.toLowerCase()} found
          </EmptyText>
        ) : (
          <>
            {upcomingEventsToShow.map((event: EventType) => (
              <ListItem key={event.id}>
                <EventListItem event={event} variant={undefined} />
              </ListItem>
            ))}
            {upcomingEvents.length > EVENT_COUNTS_TO_SHOW ? (
              <ShowMoreLessButton
                showAll={showAllUpcomingEvents}
                setShowAll={setShowAllUpcomingEvents}
              />
            ) : null}
          </>
        )}
      </List>
    </SkeletonList>
  );

  return (
    <ScreenWrapper>
      <Heading size={{ base: "2xl", md: "lg" }} fontWeight={700} as={"h1"}>
        {localeEventsWords}
      </Heading>

      <SkeletonGrid
        isLoaded={!isLoading}
        gridSize={4}
        skeleton={<StatsCardSkeleton />}
      >
        <Grid
          templateColumns={{ base: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
          templateRows={{ base: "repeat(2, 1fr)", lg: "repeat(1, 1fr)" }}
          gap={6}
        >
          {summaryStats.map((stat) => (
            <GridItem key={stat.id} w={"100%"}>
              <StatsCard stat={stat} icon={BarChartIcon} />
            </GridItem>
          ))}
        </Grid>
      </SkeletonGrid>

      {/* Desktop Layout */}
      <Show above={"sm"}>
        <Flex direction={"column"} gap={8}>
          {pastEvents.length > 0 && (
            <Flex direction={"column"} gap={6}>
              <Flex direction={"row"} justify={"space-between"}>
                <HStack gap={3}>
                  <Heading fontSize={"xl"} as={"h2"}>
                    Past {localeEventsWords}
                  </Heading>
                  <TotalCountBadge count={pastEvents?.length} />
                </HStack>
                <MultiSelectInput
                  placeholder={`${generateSingularLabel(
                    localeEventsWords,
                    1,
                  )} type`}
                  options={EVENT_OPTIONS}
                  value={pastEventFilters}
                  menuLabel={"Filter by"}
                  onChange={setPastEventFilters}
                />
              </Flex>
              {renderPastEvents()}
            </Flex>
          )}

          <Flex direction={"column"} gap={6}>
            <Flex direction={"row"} justify={"space-between"}>
              <HStack gap={3}>
                <Heading fontSize={"xl"} as={"h2"}>
                  Upcoming {localeEventsWords}
                </Heading>
                <TotalCountBadge count={upcomingEvents?.length} />
              </HStack>
              {upcomingEvents.length > 0 && (
                <MultiSelectInput
                  placeholder={`${generateSingularLabel(
                    localeEventsWords,
                    1,
                  )} type`}
                  options={EVENT_OPTIONS}
                  value={upcomingEventFilters}
                  menuLabel={"Filter by"}
                  onChange={setUpcomingEventFilters}
                />
              )}
            </Flex>
            {upcomingEvents.length === 0 ? (
              <Empty
                icon={<Icon as={MissingDates} boxSize={"auto"} />}
                emptyMessage={`No ${localeEventsWords.toLocaleLowerCase()} currently booked`}
                emptyDesc={
                  "Private inspections and open homes will appear here once scheduled"
                }
              />
            ) : (
              renderUpcomingEvents()
            )}
          </Flex>
        </Flex>
      </Show>

      {/* Mobile Layout */}
      <Show below={"sm"}>
        <Tabs variant={"soft-rounded"}>
          <TabList overflowX={"scroll"}>
            {pastEvents.length > 0 && (
              <Tab whiteSpace={"nowrap"}>Past {localeEventsWords}</Tab>
            )}
            <Tab whiteSpace={"nowrap"}> Upcoming {localeEventsWords}</Tab>
          </TabList>

          <TabPanels>
            {pastEvents.length > 0 && (
              <TabPanel>
                <MultiSelectInput
                  placeholder={`${generateSingularLabel(
                    localeEventsWords,
                    1,
                  )} type`}
                  options={EVENT_OPTIONS}
                  value={mobileFilters}
                  menuLabel={"Filter by"}
                  onChange={setMobileFilters}
                  menuButtonProps={{
                    mb: 3,
                    w: "100%",
                  }}
                />
                {renderPastEvents()}
              </TabPanel>
            )}
            <TabPanel>
              {upcomingEvents.length === 0 ? (
                <Empty
                  icon={<Icon as={MissingDates} boxSize={"auto"} />}
                  emptyMessage={`No ${localeEventsWords.toLocaleLowerCase()} currently booked`}
                  emptyDesc={
                    "Private inspections and open homes will appear here once scheduled"
                  }
                />
              ) : (
                <>
                  <MultiSelectInput
                    placeholder={`${generateSingularLabel(
                      localeEventsWords,
                      1,
                    )} type`}
                    options={EVENT_OPTIONS}
                    value={mobileFilters}
                    menuLabel={"Filter by"}
                    onChange={setMobileFilters}
                    menuButtonProps={{
                      mb: 3,
                      w: "100%",
                    }}
                  />
                  {renderUpcomingEvents()}
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Show>
    </ScreenWrapper>
  );
}
